import { defineStore } from 'pinia'
import { fetchProducts } from '@/services/api'

export function mapProduct(p) {
    return {
        id: p.id,
        image1: p.image1,
        image2: p.image2,
        images: p.images,
        name: p.name,
        description: p.description,
        review: '0 Reviews',
        rate: ['fas fa-star','fas fa-star','fas fa-star','fas fa-star','fas fa-star'],
        price: typeof p.price === 'number'
            ? p.price
            : parseFloat(String(p.price).replace(/[^\d.,-]/g, '').replace(',', '.')) || 0,
        tag: p.tag,
        des: false,
        desRate: '0',
        externalUrl: p.externalUrl,
        creator: p.creator,
        purchaseLink: p.purchase_link,
        brancheId: p.branche_id,
        createDate: p.created_at,
        artistId: p.artist_id,
        artistName: p.artist_name,
        artistPersonName: p.artist_name_personal,
        artistCity: p.artist_city,
        artistLogo: p.artist_logo,
        artistImage: p.artist_image,
        artistBanner: p.artist_banner,
        artistShortDescription: p.artist_bio_short,
        artistDescription: p.artist_bio_long,
        artistExternalUrl: p.artist_url,
        artistSubdomain: p.artist_subdomain,
        artistSoldOutHidePrice: p.artist_sold_out_hide_price
    }
}

export function makeSignature(mode, params = {}) {
    const {
        category_id = null,
        artist_id = null,
        branche_id = null,
        q = '',
        sort = 'random',
        seed = null,
    } = params

    return JSON.stringify({
        mode,
        category_id,
        artist_id,
        branche_id,
        q,
        sort,
        seed,
    })
}

export const useProductStore = defineStore('products', {
    state: () => ({
        productData: [],
        isLoading: false,
        lastBatchCount: 0,
        lastQuery: {
            mode: 'all',
            params: {
                limit: 24,
                offset: 0,
                sort: 'random',
                seed: null,
                category_id: null,
                artist_id: null,
                q: '',
            },
        },
        currentSignature: makeSignature('all', { sort: 'random', seed: null, q: '' }),
        requestId: 0,
    }),

    getters: {
        hasProducts: (s) => s.productData.length > 0,
        hasMore: (s) => {
            const paginated = s.lastQuery.mode === 'all' || s.lastQuery.mode === 'categories'
            const pageSize = s.lastQuery.params?.limit ?? 24
            return paginated && s.lastBatchCount === pageSize
        },
    },

    actions: {
        async loadProducts({ mode, params = {}, append = false } = {}) {
            const query = { mode, ...params }
            const newSignature = makeSignature(mode, params)

            const contextChanged = newSignature !== this.currentSignature
            if (!append && contextChanged) {
                this.productData = []
                this.lastBatchCount = 0
            }

            this.isLoading = true
            const myReq = ++this.requestId

            try {
                const apiProducts = await fetchProducts(query)
                if (myReq !== this.requestId) return

                const mapped = Array.isArray(apiProducts) ? apiProducts.map(mapProduct) : []

                if (append) {
                    this.productData.push(...mapped)
                } else {
                    this.productData = mapped
                }

                this.lastBatchCount = mapped.length

                this.lastQuery = {
                    mode,
                    params: {
                        limit: params.limit ?? this.lastQuery.params.limit ?? 24,
                        offset: params.offset ?? 0,
                        sort: params.sort ?? 'random',
                        seed: params.seed ?? null,
                        category_id: params.category_id ?? null,
                        artist_id: params.artist_id ?? null,
                        q: params.q ?? this.lastQuery.params.q ?? '',
                        ...params,
                    },
                }
                this.currentSignature = newSignature
            } finally {
                if (myReq === this.requestId) {
                    this.isLoading = false
                }
            }
        },

        async loadCategory({ categoryId, limit = 24, offset = 0, sort = 'random', seed = null, q = '' } = {}) {
            await this.loadProducts({
                mode: 'categories',
                params: { category_id: categoryId, limit, offset, sort, seed, q },
                append: offset > 0,
            })
        },

        async loadStartPage(){
            await this.loadProducts({
                mode: 'startpage',
            })
        },

        async loadAll({ limit = 24, offset = 0, sort = 'random', seed = null, q = '' } = {}) {
            await this.loadProducts({
                mode: 'all',
                params: { limit, offset, sort, seed, q },
                append: offset > 0,
            })
        },

        async loadMore({ add = 24 } = {}) {
            const { mode, params } = this.lastQuery
            const paginated = mode === 'all' || mode === 'categories'
            if (!paginated) return

            const nextOffset = (params.offset ?? 0) + (params.limit ?? add)
            const limit = params.limit ?? add

            await this.loadProducts({
                mode,
                params: { ...params, offset: nextOffset, limit },
                append: true,
            })
        },
    },

    persist: {
        storage: sessionStorage,
        paths: ['productData', 'lastQuery', 'currentSignature'],
    },
})